import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl_SPB } from '../../BaseUrl';

const { Text } = Typography;

const currency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(num);
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Profit forecast banner for the trade job form. Calls the backend profit tracker,
 * which derives income from the linked invoice, material cost from invoice line items
 * matched to inventory (purchasePrice x qty), and labour cost from hours worked per
 * resource priced at their award/salary rate, then shows whether the job is profitable.
 *
 * The calculation runs against persisted data (invoice, time allocations, status
 * history), so it is only meaningful once the job has been saved.
 */
export default function ProfitForecastView({ token, jobId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const fetchForecast = useCallback(() => {
    if (!jobId || !token) return;
    setLoading(true);
    setError('');
    axios
      .get(`${baseUrl_SPB}/api/v1/accounting/profittracker/job`, {
        params: { jobId },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => setData(response.data))
      .catch((err) => {
        console.error('Failed to fetch profit forecast:', err);
        setError('Could not load the profit forecast right now.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [jobId, token]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  // New, unsaved job — nothing to calculate against yet.
  if (!jobId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Profit forecast"
        description="Save the job (with its invoice and assigned staff) to see whether it is forecast to make a profit."
        style={{ marginBottom: 8 }}
      />
    );
  }

  if (loading && !data) {
    return (
      <Card size="small" style={{ marginBottom: 8 }}>
        <Spin /> <Text type="secondary" style={{ marginLeft: 8 }}>Calculating profit forecast…</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={<Button size="small" onClick={fetchForecast}>Retry</Button>}
        style={{ marginBottom: 8 }}
      />
    );
  }

  if (!data) return null;

  const profit = num(data.profit);
  const isProfit = profit > 0;
  const isLoss = profit < 0;

  const verdict = isProfit
    ? { color: 'green', label: 'Forecast: PROFIT' }
    : isLoss
      ? { color: 'red', label: 'Forecast: LOSS' }
      : { color: 'default', label: 'Forecast: BREAK-EVEN' };

  const profitColor = isProfit ? '#3f8600' : isLoss ? '#cf1322' : undefined;

  const materialColumns = [
    { title: 'Item', dataIndex: 'name', key: 'name' },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', align: 'right', render: (v) => num(v) },
    { title: 'Unit cost', dataIndex: 'unitPurchasePrice', key: 'unitPurchasePrice', align: 'right', render: currency },
    { title: 'Line cost', dataIndex: 'lineCost', key: 'lineCost', align: 'right', render: currency },
  ];

  const labourColumns = [
    { title: 'Staff', dataIndex: 'staffName', key: 'staffName', render: (v, r) => v || r.staffId },
    { title: 'Hours', dataIndex: 'hours', key: 'hours', align: 'right', render: (v) => num(v) },
    { title: 'Rate', dataIndex: 'hourlyRate', key: 'hourlyRate', align: 'right', render: currency },
    { title: 'Cost', dataIndex: 'cost', key: 'cost', align: 'right', render: currency },
    {
      title: 'Source',
      key: 'source',
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {r.rateSource}{r.hoursSource ? ` · ${r.hoursSource}` : ''}
        </Text>
      ),
    },
  ];

  const unmatched = Array.isArray(data.unmatchedItems) ? data.unmatchedItems : [];
  const notes = Array.isArray(data.notes) ? data.notes : [];
  const materialLines = Array.isArray(data.materialLines) ? data.materialLines : [];
  const labourLines = Array.isArray(data.labourLines) ? data.labourLines : [];

  return (
    <Card
      size="small"
      style={{ marginBottom: 8, borderColor: profitColor }}
      title={
        <span>
          <Tag color={verdict.color}>{verdict.label}</Tag>
          {data.marginPercent != null && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Margin {num(data.marginPercent).toFixed(1)}%
            </Text>
          )}
        </span>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={fetchForecast}>
          Recalculate
        </Button>
      }
    >
      <Row gutter={16}>
        <Col xs={12} sm={6}>
          <Statistic title="Income (ex-GST)" value={num(data.income)} formatter={() => currency(data.income)} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic title="Material cost" value={num(data.materialCost)} formatter={() => currency(data.materialCost)} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic title="Labour cost" value={num(data.labourCost)} formatter={() => currency(data.labourCost)} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Profit"
            value={profit}
            precision={2}
            valueStyle={{ color: profitColor }}
            formatter={() => currency(profit)}
          />
        </Col>
      </Row>

      <Collapse
        ghost
        size="small"
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'details',
            label: <Text type="secondary" style={{ fontSize: 12 }}>Breakdown details</Text>,
            children: (
              <>
                {materialLines.length > 0 && (
                  <>
                    <Text strong style={{ fontSize: 12 }}>Materials</Text>
                    <Table
                      size="small"
                      rowKey={(r, i) => `mat-${i}`}
                      columns={materialColumns}
                      dataSource={materialLines}
                      pagination={false}
                      style={{ marginBottom: 12 }}
                    />
                  </>
                )}

                {labourLines.length > 0 && (
                  <>
                    <Text strong style={{ fontSize: 12 }}>Labour</Text>
                    <Table
                      size="small"
                      rowKey={(r, i) => `lab-${i}`}
                      columns={labourColumns}
                      dataSource={labourLines}
                      pagination={false}
                      style={{ marginBottom: 12 }}
                    />
                  </>
                )}

                {unmatched.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message={`${unmatched.length} invoice line item(s) not matched to inventory (excluded from material cost)`}
                    description={unmatched.map((u, i) => (
                      <div key={i} style={{ fontSize: 12 }}>{u.name}</div>
                    ))}
                  />
                )}

                {notes.length > 0 && (
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {notes.map((n, i) => (
                      <div key={i}>• {n}</div>
                    ))}
                  </div>
                )}

                {materialLines.length === 0 && labourLines.length === 0 && unmatched.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>No cost lines to show.</Text>
                )}
              </>
            ),
          },
        ]}
      />
    </Card>
  );
}
